import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import DataGrid from '../../../../../common/DataGrid/DataGrid.jsx';
import { Spinner, Tooltip } from '../../../../../common/index.jsx';
import { useDataGridTextFilter } from '../../../../../common/DataGrid/filters/useDataGridTextFilter.jsx';
import {
  dataGridTextSorter,
  dataGridNumberSorter,
  dataGridDateSorter,
  dataGridSurnameSorter,
} from '../../../../../common/DataGrid/sorters/DataGridSorters.jsx';
import dayjs from 'dayjs';
import { DATETIME_FORMAT_DATETIME } from '../../../../../../utils/helpers/CommonHelper.jsx';
import { IconButton } from '../../../../../common/controls/Controls.jsx';
import {
  EyeOutlined,
  QrcodeOutlined,
  CopyOutlined,
  BarChartOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  useGetQuizQuestionsQuery,
  useDeleteQuizQuestionMutation,
} from '../../../../../../redux/services/quiz_questions.jsx';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../../../../../utils/helpers/ApiClient.jsx';
import { useToastMessages } from '../../../../../../hooks/useToastMessages.jsx';
import QuizQuestionPreviewModal from '../Modals/QuizQuestionPreviewModal.jsx';

const QuizQuestionsNested = ({ quizId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, refetch } = useGetQuizQuestionsQuery(quizId);
  const [deleteQuestion] = useDeleteQuizQuestionMutation();

  const [previewId, setPreviewId] = useState(null);
  const { successMessage, errorMessage } = useToastMessages();

  const rows = (
    Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : []
  ).map((r) => ({
    ...r,
    answers_count:
      typeof r.answers_count === 'number'
        ? r.answers_count
        : Array.isArray(r.answers)
          ? r.answers.length
          : 0,
  }));

  const textFilter = useDataGridTextFilter('text');

  const columns = [
    {
      title: t('Otázka'),
      dataIndex: 'text',
      ellipsis: true,
      sorter: dataGridTextSorter('text'),
      ...textFilter,
    },
    {
      title: t('Počet odpovedí'),
      dataIndex: 'answers_count',
      width: 160,
      sorter: dataGridNumberSorter('answers_count'),
    },
    {
      title: t('Vytvorené'),
      dataIndex: 'created_at',
      width: 200,
      sorter: dataGridDateSorter('created_at'),
      render: (created_at) => (
        <Tooltip tooltip={dayjs(created_at).format(DATETIME_FORMAT_DATETIME)}>
          <div>{dayjs(created_at).format(DATETIME_FORMAT_DATETIME)}</div>
        </Tooltip>
      ),
    },
  ];

  const handleDelete = async (row) => {
    try {
      await deleteQuestion(row.id).unwrap();
      await refetch();
    } catch (e) {
      // optional toast
    }
  };

  const openQrByUrl = (id) => {
    navigate(`${routes.cmsQuizQuestionQR}/${id}`);
  };

  const copyQrUrl = async (id) => {
    const link = `${window.location.origin}${routes.cmsQuizQuestionAnswer}/${id}`;
    try {
      await navigator.clipboard.writeText(link);
      successMessage({
        message: t('Odkaz pre študenta bol skopírovaný do schránky.'),
      });
    } catch (e) {
      errorMessage({ message: t('Kopírovanie odkazu zlyhalo.') });
    }
  };

  const customSettings = (row) => (
    <>
      <IconButton
        icon={<EyeOutlined />}
        tooltip={t('Zobraziť náhľad otázky')}
        onClick={(e) => {
          e.stopPropagation();
          setPreviewId(row.id);
        }}
      />
      <IconButton
        icon={<QrcodeOutlined />}
        tooltip={t('Spustiť otázku pomocou QR kódu')}
        onClick={(e) => {
          e.stopPropagation();
          openQrByUrl(row.id);
        }}
      />
      <IconButton
        icon={<CopyOutlined />}
        tooltip={t('Skopírovať odkaz pre študenta')}
        onClick={async (e) => {
          e.stopPropagation();
          await copyQrUrl(row.id);
        }}
      />
      <IconButton
        icon={<BarChartOutlined />}
        tooltip={t('Zobraziť odpovede')}
        onClick={(e) => {
          e.stopPropagation();
          navigate(`${routes.cmsQuizQuestionResults}/${row.id}`);
        }}
      />
    </>
  );

  if (isLoading) return <Spinner />;

  return (
    <>
      <DataGrid
        rowKey={(row) => row.id}
        columns={columns}
        dataSource={rows}
        permissionsBasic={'quiz-questions'}
        uniqueTableId={`quiz-questions-nested-${quizId}`}
        onEditClick={(row) =>
          navigate(`${routes.cmsQuizQuestionsEdit}/${row.id}`)
        }
        onDelete={(row) => handleDelete(row)}
        customSettings={customSettings}
        onRowDetailClick={(row) => setPreviewId(row.id)}
        disableExpandRow={() => true}
        allowClick={false}
        secondaryCondition={(row) => (row.student_answer_count || 0) === 0}
        editDisabledTooltip={(row) =>
          (row.student_answer_count || 0) > 0
            ? t('Už existujú odpovede – úprava nie je možná.')
            : ''
        }
        deleteCondition={(row) => (row.student_answer_count || 0) === 0}
        deleteDisabledTooltip={(row) =>
          (row.student_answer_count || 0) > 0
            ? t('Už existujú odpovede – mazanie nie je možné.')
            : ''
        }
      />
      <QuizQuestionPreviewModal
        open={!!previewId}
        questionId={previewId}
        onClose={() => setPreviewId(null)}
      />
    </>
  );
};

QuizQuestionsNested.propTypes = {
  quizId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

const QuizzesTable = ({
  quizzes,
  onDelete,
  onDetailClick,
  onAddQuestion,
  expandedRowKeys,
  onExpandedRowsChange,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const titleFilter = useDataGridTextFilter('title');
  const subjectFilter = useDataGridTextFilter('study_subject_name');
  const creatorFilter = useDataGridTextFilter('creator_full_name');

  const columns = useMemo(
    () => [
      {
        title: t('Názov'),
        dataIndex: 'title',
        ellipsis: true,
        sorter: dataGridTextSorter('title'),
        defaultSortOrder: 'ascend',
        ...titleFilter,
      },
      {
        title: t('Predmet'),
        dataIndex: 'study_subject_name',
        ellipsis: true,
        sorter: dataGridTextSorter('study_subject_name'),
        ...subjectFilter,
      },
      {
        title: t('Autor'),
        dataIndex: 'creator_full_name',
        ellipsis: true,
        sorter: dataGridSurnameSorter('creator_full_name'),
        ...creatorFilter,
      },
      {
        title: t('Počet otázok'),
        dataIndex: 'questions_count',
        width: 140,
        sorter: dataGridNumberSorter('questions_count'),
      },
      {
        title: t('Vytvorené'),
        dataIndex: 'created_at',
        width: 180,
        sorter: dataGridDateSorter('created_at'),
        render: (created_at) => (
          <Tooltip tooltip={dayjs(created_at).format(DATETIME_FORMAT_DATETIME)}>
            <div>{dayjs(created_at).format(DATETIME_FORMAT_DATETIME)}</div>
          </Tooltip>
        ),
      },
    ],
    [t, titleFilter, subjectFilter, creatorFilter],
  );

  const data = Array.isArray(quizzes)
    ? quizzes
    : Array.isArray(quizzes?.data)
      ? quizzes.data
      : [];

  const customSettings = (row) => (
    <IconButton
      icon={<PlusOutlined />}
      tooltip={t('Pridať otázku')}
      onClick={(e) => {
        e.stopPropagation();
        onAddQuestion && onAddQuestion(row);
      }}
    />
  );

  const renderNested = (row) => (
    <div style={{ padding: '8px 16px' }}>
      <QuizQuestionsNested quizId={row.id} />
    </div>
  );

  return (
    <DataGrid
      rowKey={(row) => row.id}
      columns={columns}
      dataSource={data}
      permissionsBasic={'quizzes'}
      uniqueTableId={'quizzes-table'}
      onEditClick={onDetailClick}
      onRowClick={onDetailClick}
      onDelete={onDelete}
      customSettings={(row) => (
        <>
          {customSettings(row)}
          <IconButton
            icon={<BarChartOutlined />}
            tooltip={t('Zobraziť výsledky')}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`${routes.cmsQuizResults}/${row.id}`);
            }}
          />
        </>
      )}
      customExpandedRowRender={renderNested}
      disableExpandRow={() => false}
      expandedRowKeys={expandedRowKeys}
      onExpand={(expanded, record) => {
        const newKeys = expanded
          ? [...expandedRowKeys, record.id]
          : expandedRowKeys.filter((key) => key !== record.id);
        onExpandedRowsChange && onExpandedRowsChange(newKeys);
      }}
    />
  );
};

QuizzesTable.propTypes = {
  quizzes: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  onDelete: PropTypes.func,
  onDetailClick: PropTypes.func,
  onAddQuestion: PropTypes.func,
  expandedRowKeys: PropTypes.array,
  onExpandedRowsChange: PropTypes.func,
};

export default QuizzesTable;
